import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { panelHeadSx, panelSx, panelTitleSx } from 'shared/config/Styles';
import DynamicToggleButton from 'shared/ui/ToggleButton';
import { FILE_KIND, FILE_KIND_LABEL, fileKindOf, sheetIndexFor, workbookToSheets } from '../../../lib/packingListFile';
import { describeSourceRef } from '../../../lib/packingListWire';
import PdfCanvasViewer from './PdfCanvasViewer';

const HIGHLIGHT = 'rgba(255, 193, 7, 0.45)';
const HIGHLIGHT_CELL = 'rgba(255, 179, 0, 0.75)';

/** 선택한 라인을 원본 어디서 읽었는지(페이지·시트·행·셀·읽은 글) 한 줄로 알린다. 수기로 더한 행은 위치가 없다. */
function SourceStrip({ activeRef, lines }) {
    if (!activeRef) return null;
    const line = lines.find((l) => l.id === activeRef.lineId);
    const lineNo = line?.line_no ?? '';
    const where = describeSourceRef(line?.source_ref);
    return (
        <Box data-testid="pl-source-strip" sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid #e6ebee', bgcolor: where ? 'rgba(255,193,7,0.12)' : '#f8f8f8', display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#003a52', flexShrink: 0 }}>Line {lineNo}</Typography>
            {where ? (
                <>
                    <Typography sx={{ fontSize: 11, color: '#003a52', flexShrink: 0 }}>{where}</Typography>
                    {line.source_ref.text && <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic', minWidth: 0 }} noWrap title={line.source_ref.text}>“{line.source_ref.text}”</Typography>}
                </>
            ) : (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Added manually · no location in the original</Typography>
            )}
        </Box>
    );
}


/** 이미지 PL. 좌표 박스를 이미지 위에 덧그린다. 불러오지 못하면(만료 등) 알려서 URL을 다시 받게 한다. */
function ImageViewer({ fileUrl, lines, activeRef, onPickLine, onLoadError }) {
    return (
        <Box sx={{ position: 'relative', display: 'inline-block', minWidth: '100%' }}>
            <img src={fileUrl} alt="packing list" onError={onLoadError} style={{ width: '100%', display: 'block' }} />
            {lines.filter((l) => l.source_ref?.box).map((l) => {
                const { x, y, w, h } = l.source_ref.box;
                const active = activeRef?.lineId === l.id;
                return <Box key={l.id} onClick={() => onPickLine(l.id)} sx={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, bgcolor: active ? HIGHLIGHT : 'rgba(0,100,126,0.08)', border: active ? '2px solid #ffb300' : '1px dashed rgba(0,100,126,0.4)', cursor: 'pointer' }} />;
            })}
        </Box>
    );
}

/** 라인의 원문 위치가 이 시트인지. 시트 이름이 없으면(단일 시트 문서) 어느 시트든 해당한다. */
const refOnSheet = (ref, sheet) => Boolean(ref) && (!ref.sheet || !sheet?.name || String(ref.sheet).trim().toLowerCase() === String(sheet.name).trim().toLowerCase());

/** 시트 격자. 선택 라인이 읽힌 행·셀을 칠하고 그 행으로 스크롤하며, 어느 라인의 행·셀을 누르면 그 라인이 선택된다. */
function SheetViewer({ sheet, lines, activeRef, onPickLine }) {
    const rowRefs = useRef({});
    const { lineByRow, lineByCell } = useMemo(() => {
        const byRow = {};
        const byCell = {};
        lines.forEach((l) => {
            if (!refOnSheet(l.source_ref, sheet)) return;
            (l.source_ref.rows ?? []).forEach((r) => {
                if (!(r in byRow)) byRow[r] = l.id;
            });
            (l.source_ref.cells ?? []).forEach((c) => {
                if (!(c in byCell)) byCell[c] = l.id;
            });
        });
        return { lineByRow: byRow, lineByCell: byCell };
    }, [lines, sheet]);
    const active = refOnSheet(activeRef, sheet) ? activeRef : null;
    const activeRows = new Set(active?.rows ?? []);
    const activeCells = new Set(active?.cells ?? []);
    const firstRow = active?.rows?.length ? Math.min(...active.rows) : null;
    useEffect(() => {
        if (firstRow != null) rowRefs.current[firstRow]?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, [firstRow, activeRef?.lineId]);
    return (
        <Box>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11, background: 'white' }}>
                <thead>
                    <tr>
                        <th style={{ border: '1px solid #dfdfdf', background: '#f1f1f1', width: 32 }} />
                        {sheet.columns.map((c) => <th key={c} style={{ border: '1px solid #dfdfdf', background: '#f1f1f1', padding: 2, fontWeight: 500 }}>{c}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {sheet.rows.map((r) => {
                        const rowActive = activeRows.has(r.row);
                        const rowLineId = lineByRow[r.row];
                        return (
                            <tr key={r.row} ref={(el) => (rowRefs.current[r.row] = el)} data-testid={rowActive ? 'pl-sheet-row-active' : undefined} style={{ background: rowActive ? HIGHLIGHT : 'transparent' }}>
                                <td onClick={() => rowLineId && onPickLine(rowLineId)} style={{ border: '1px solid #dfdfdf', background: '#f1f1f1', textAlign: 'center', color: '#7a7a7a', cursor: rowLineId ? 'pointer' : 'default' }}>{r.row}</td>
                                {r.cells.map((cell, idx) => {
                                    const addr = `${sheet.columns[idx]}${r.row}`;
                                    const lineId = lineByCell[addr] ?? rowLineId;
                                    const cellActive = activeCells.has(addr);
                                    return (
                                        <td key={addr} data-testid={cellActive ? 'pl-sheet-cell-active' : undefined} onClick={() => lineId && onPickLine(lineId)} style={{ border: '1px solid #dfdfdf', padding: '2px 4px', whiteSpace: 'nowrap', cursor: lineId ? 'pointer' : 'default', textDecoration: addr in lineByCell ? 'underline dotted' : 'none', background: cellActive ? HIGHLIGHT_CELL : undefined, fontWeight: cellActive ? 600 : 400 }}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {sheet.truncated && <Typography sx={{ fontSize: 11, color: 'text.secondary', p: 1 }}>Only the first {sheet.rows.length} rows are shown. Download the file to see the rest.</Typography>}
        </Box>
    );
}

/** 시트가 여럿인 워크북. 탭으로 고르고, 선택 라인이 다른 시트에 있으면 그 시트로 옮긴다(CIPL은 PL 시트가 뒤에 오기 쉽다). */
function SheetsViewer({ sheets, lines, activeRef, onPickLine }) {
    const [index, setIndex] = useState(0);
    const wanted = sheetIndexFor(sheets, activeRef?.sheet);
    useEffect(() => {
        if (wanted != null) setIndex(wanted);
    }, [wanted, activeRef?.lineId]);
    const current = sheets[Math.min(index, sheets.length - 1)];
    return (
        <Box>
            {sheets.length > 1 && (
                <Box role="tablist" sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    {sheets.map((sh, i) => (
                        <Chip key={`${sh.name}-${i}`} role="tab" aria-selected={i === index} size="small" label={sh.name || `Sheet ${i + 1}`} onClick={() => setIndex(i)} variant={i === index ? 'filled' : 'outlined'} color={i === index ? 'primary' : 'default'} sx={{ fontSize: 10, height: 20 }} />
                    ))}
                </Box>
            )}
            <SheetViewer sheet={current} lines={lines} activeRef={activeRef} onPickLine={onPickLine} />
        </Box>
    );
}

/** .xlsx 원본을 받아 시트별 격자로 그린다(화면용 PDF가 없을 때). 받기에 실패하면(만료 등) URL을 다시 받게 한다. */
function WorkbookViewer({ fileUrl, lines, activeRef, onPickLine, onLoadError }) {
    const [state, setState] = useState({ status: 'loading', sheets: null, error: null });
    useEffect(() => {
        const controller = new AbortController();
        setState({ status: 'loading', sheets: null, error: null });
        (async () => {
            try {
                const response = await fetch(fileUrl, { signal: controller.signal });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const sheets = await workbookToSheets(await response.arrayBuffer());
                if (!controller.signal.aborted) setState({ status: 'ready', sheets, error: null });
            } catch (error) {
                if (controller.signal.aborted) return;
                setState({ status: 'error', sheets: null, error: error?.message ?? 'Could not read the file.' });
                onLoadError?.();
            }
        })();
        return () => controller.abort();
        // onLoadError는 매 렌더마다 바뀌는 함수라 의존에 넣으면 같은 URL을 다시 받는다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileUrl]);
    if (state.status === 'loading') return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
    if (state.status === 'error') return <Box sx={{ p: 2, fontSize: 12, color: '#5a6b74' }}>Could not open this spreadsheet in the viewer ({state.error}). Download it to open in Excel.</Box>;
    if (!state.sheets?.length) return <Box sx={{ p: 2, fontSize: 12, color: '#5a6b74' }}>This workbook has no sheet to show.</Box>;
    return <SheetsViewer sheets={state.sheets} lines={lines} activeRef={activeRef} onPickLine={onPickLine} />;
}

/** 파서가 읽어낸 문서를 종이 모양으로 다시 그린다. 원본 파일이 없거나(수기·붙여넣기) 원문 대조가 어려울 때 쓴다. */
function ExtractedViewer({ raw, lines, activeRef, onPickLine, filename }) {
    const rowRefs = useRef({});
    useEffect(() => {
        const idx = lines.findIndex((l) => l.id === activeRef?.lineId);
        if (idx >= 0) rowRefs.current[idx]?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }, [activeRef, lines]);
    if (!raw) {
        return (
            <Box sx={{ py: 10, textAlign: 'center', color: '#5a6b74' }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 32, color: '#7a7a7a' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>No extracted document for {filename || 'this draft'}</Typography>
                <Typography sx={{ fontSize: 11, color: '#7a7a7a' }}>Enter the lines in the grid on the right.</Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ bgcolor: 'white', boxShadow: 1, borderRadius: 1, p: 2.5, maxWidth: 860, mx: 'auto', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#003a52', textAlign: 'center', borderBottom: '1px solid #dfdfdf', pb: 1 }}>{raw.title}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', p: 1.5, bgcolor: '#fafafa', borderRadius: 1, fontSize: 11 }}>
                {Object.entries(raw.header_fields ?? {}).map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', minWidth: 0 }}>
                        <span style={{ fontWeight: 500, color: '#5a6b74', width: 128, flexShrink: 0 }}>{k}</span>
                        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(v)}>{v}</span>
                    </Box>
                ))}
            </Box>
            <Box sx={{ overflowX: 'auto', border: '1px solid #dfdfdf', borderRadius: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 580 }}>
                    <thead>
                        <tr style={{ background: '#f1f1f1', color: '#003a52' }}>
                            {(raw.table_headers ?? []).map((th, i) => <th key={i} style={{ padding: 8, textAlign: 'left', borderRight: '1px solid #dfdfdf', borderBottom: '1px solid #dfdfdf', whiteSpace: 'nowrap', fontWeight: 500 }}>{th}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {(raw.table_rows ?? []).map((row, rowIdx) => {
                            const line = lines[rowIdx];
                            const active = line && activeRef?.lineId === line.id;
                            return (
                                <tr key={rowIdx} ref={(el) => (rowRefs.current[rowIdx] = el)} onClick={() => line && onPickLine(line.id)} style={{ cursor: line ? 'pointer' : 'default', background: active ? 'rgba(0,100,126,0.08)' : 'transparent', outline: active ? '1px solid #00647e' : 'none', fontWeight: active ? 500 : 400 }}>
                                    {row.map((cell, cIdx) => <td key={cIdx} style={{ padding: 8, borderRight: '1px solid #dfdfdf', borderBottom: '1px solid #dfdfdf', fontFamily: 'inherit', textAlign: cIdx === 0 ? 'center' : 'left', color: cIdx === 0 ? '#5a6b74' : 'inherit' }}>{cell}</td>)}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Box>
            {raw.footnotes?.length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#003a52' }}>Footnotes</Typography>
                    {raw.footnotes.map((f, i) => <Typography key={i} sx={{ fontSize: 11 }}>{f}</Typography>)}
                </Box>
            )}
            {raw.bottom_text && (
                <Box sx={{ p: 1.5, bgcolor: 'rgba(0,100,126,0.06)', borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#003a52' }}>Container / seal text at the bottom of the document</Typography>
                    <Typography sx={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{raw.bottom_text}</Typography>
                </Box>
            )}
        </Box>
    );
}

/** 원본이 있어야 하는데 아직 못 받았거나 못 여는 상태를 본문 자리에 알린다. */
function OriginalNotice({ children, action }) {
    return (
        <Box sx={{ py: 8, textAlign: 'center', color: '#5a6b74', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 32, color: '#7a7a7a' }} />
            <Typography sx={{ fontSize: 12 }}>{children}</Typography>
            {action}
        </Box>
    );
}

/**
 * 원본 문서 뷰어. 상단 툴바에 파일명·Original/Extracted 전환·다운로드·확대축소를 두고,
 * 본문은 파일 종류(PDF·이미지·시트)나 파서 추출본을 그려 그리드와 양방향으로 라인을 짚는다.
 *
 * file은 usePackingListFileUrl 결과(status·file{url, preview_url, mime_type, original_filename, source, expires_at}·expired·refresh)다.
 * - 수기 문서(file.status === 'none')는 원본 영역 없이 추출본만 보여준다.
 * - 엑셀 원본(.xlsx·.xls)은 화면용 PDF(preview_url)가 있으면 엑셀 모양 그대로 보여준다. 만드는 중이면 준비 중 안내,
 *   끝내 없으면 .xlsx는 첫 시트를 격자로, .xls는 다운로드 안내만 둔다.
 * - 추출본(raw)이 없는 문서(파싱 대기·실패)는 전환 없이 원본만 보여준다. 원본을 받는 중·못 받은 상태도 그 자리에 알린다.
 * - 수정(edit) 중인 문서는 서버가 현재 확정 원본을 주므로(source 'confirmed') 그 사실을 배지로 알린다.
 * - URL이 만료되면 이미 그려진 원본은 그대로 두고 Reload로 재발급한다. 이미지·시트는 불러오기에 실패하면 만료 시 스스로 재발급하고, 아니면 Retry를 둔다.
 * 원문 위치는 PDF면 페이지로 넘기고 격자면 행·셀을 칠하며, 툴바 아래 한 줄에 위치와 읽은 글을 적는다. 수기 행은 위치를 표시하지 않는다.
 */
export default function DocumentViewer({ doc, raw, lines, activeRef, onPickLine, file = null, onDownload = null, downloading = false }) {
    const sheet = raw?.sheet ?? null;
    /** 원본이 있어야 하는 문서인지(없는 문서는 status 'none'). 아직 요청 전(idle)·받는 중·실패도 원본 자리를 차지한다. */
    const hasOriginal = Boolean(file) && file.status !== 'none';
    const kind = fileKindOf(file?.file?.mime_type, file?.file?.original_filename ?? doc?.original_filename);
    const [mode, setMode] = useState('original');
    const [zoom, setZoom] = useState(100);
    const filename = file?.file?.original_filename ?? doc?.original_filename ?? (hasOriginal ? 'Original file' : 'Manual entry');
    /** 이미지를 불러오지 못한 URL. 만료가 아닌 실패는 Retry로 다시 받게 한다. */
    const [failedUrl, setFailedUrl] = useState(null);

    /** 이미지·시트를 불러오지 못했을 때. 만료됐다면 URL을 다시 받고, 아니면 Retry를 보여 준다(같은 URL을 다시 받아도 같은 결과다). */
    const handleLoadError = () => {
        if (!file?.file?.url) return;
        if (file.expired) file.refresh?.();
        else setFailedUrl(file.file.url);
    };

    const toggleMode = (next) => {
        if (!next) return;
        // 추출본을 보다 원본으로 돌아왔는데 그 사이 만료됐으면 새 URL로 그린다.
        if (next === 'original' && file?.expired) file.refresh?.();
        setMode(next);
    };

    const url = file?.file?.url;
    /** 엑셀 원본의 화면용 PDF. 원본 URL과 같이 발급·만료된다. */
    const previewUrl = (kind === FILE_KIND.SHEET || kind === FILE_KIND.LEGACY_SHEET) && file?.file?.preview_url ? file.file.preview_url : null;
    const retryAction = <Button size="small" startIcon={<RefreshIcon />} onClick={() => file?.refresh?.()} sx={{ fontSize: 11 }}>Retry</Button>;
    const originalBody = (() => {
        if ((file?.status === 'loading' || file?.status === 'idle') && !url) return <OriginalNotice>Loading the original…</OriginalNotice>;
        if (file?.status === 'error') return <OriginalNotice action={retryAction}>{file.error || 'Could not get the original file.'}</OriginalNotice>;
        if (!url) return sheet ? <SheetViewer sheet={sheet} lines={lines} activeRef={activeRef} onPickLine={onPickLine} /> : null;
        if (failedUrl === url) return <OriginalNotice action={retryAction}>Could not load the original. Retry to get a new link.</OriginalNotice>;
        switch (kind) {
            case FILE_KIND.PDF:
                return <PdfCanvasViewer fileUrl={url} lines={lines} activeRef={activeRef} onPickLine={onPickLine} zoom={zoom} />;
            case FILE_KIND.IMAGE:
                return <ImageViewer fileUrl={url} lines={lines} activeRef={activeRef} onPickLine={onPickLine} onLoadError={handleLoadError} />;
            case FILE_KIND.SHEET:
                if (previewUrl) return <PdfCanvasViewer fileUrl={previewUrl} lines={lines} activeRef={activeRef} onPickLine={onPickLine} zoom={zoom} />;
                if (file?.previewPending) return <OriginalNotice>Preparing the preview…</OriginalNotice>;
                return sheet ? <SheetViewer sheet={sheet} lines={lines} activeRef={activeRef} onPickLine={onPickLine} /> : <WorkbookViewer fileUrl={url} lines={lines} activeRef={activeRef} onPickLine={onPickLine} onLoadError={handleLoadError} />;
            case FILE_KIND.LEGACY_SHEET:
                if (previewUrl) return <PdfCanvasViewer fileUrl={previewUrl} lines={lines} activeRef={activeRef} onPickLine={onPickLine} zoom={zoom} />;
                if (file?.previewPending) return <OriginalNotice>Preparing the preview…</OriginalNotice>;
                return sheet ? <SheetViewer sheet={sheet} lines={lines} activeRef={activeRef} onPickLine={onPickLine} /> : <OriginalNotice>Preview is not available for .xls files. Download the file to open it in Excel.</OriginalNotice>;
            default:
                return <OriginalNotice>Preview is not supported for this file type. Download it to open.</OriginalNotice>;
        }
    })();
    /** 추출본이 없으면 전환할 것이 없으니 원본 자리(받는 중·실패 알림 포함)를 그대로 보여준다. */
    const showOriginal = (hasOriginal || Boolean(sheet)) && (mode === 'original' || !raw);
    /** PDF는 패널을 꽉 채우고 뷰어가 배율을 적용한다. 이미지·격자·추출본은 CSS로 키운다. */
    const pdfBody = showOriginal && (kind === FILE_KIND.PDF || Boolean(previewUrl)) && Boolean(url) && failedUrl !== url;

    return (
        <Box sx={{ ...panelSx, height: '100%' }}>
            <Box sx={panelHeadSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 15, color: '#00647e' }} />
                    <Typography sx={panelTitleSx} noWrap title={filename}>{filename}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{hasOriginal ? FILE_KIND_LABEL[kind] ?? 'File' : 'Extracted'}</Typography>
                    {file?.file?.source === 'confirmed' && doc?.input_source === 'edit' && (
                        <Tooltip title="An edit has no file of its own. This is the original of the current confirmed version.">
                            <Chip size="small" label="Confirmed original" sx={{ fontSize: 10, height: 18 }} />
                        </Tooltip>
                    )}
                    {hasOriginal && file.expired && (
                        <Tooltip title="The preview link has expired. Reload to get a new one if the original stops showing.">
                            <Chip size="small" color="warning" variant="outlined" icon={<RefreshIcon sx={{ fontSize: 13 }} />} label="Link expired · Reload" onClick={() => file.refresh?.()} sx={{ fontSize: 10, height: 18, cursor: 'pointer' }} />
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {(hasOriginal || sheet) && raw && (
                        <DynamicToggleButton value={showOriginal ? 'original' : 'extracted'} onChange={(_, v) => toggleMode(v)} sx={{ width: 80, fontSize: 11 }} data={[{ value: 'original', content: 'Original' }, { value: 'extracted', content: 'Extracted' }]} />
                    )}
                    {hasOriginal && onDownload && (
                        <Tooltip title="Download the original file">
                            <span>
                                <IconButton size="small" onClick={onDownload} disabled={downloading || file.status !== 'ready'} aria-label="Download original"><DownloadIcon sx={{ fontSize: 16 }} /></IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => setZoom((z) => Math.max(60, z - 10))} title="Zoom out"><ZoomOutIcon sx={{ fontSize: 15 }} /></IconButton>
                    <Typography onClick={() => setZoom(100)} sx={{ fontSize: 11, cursor: 'pointer', color: 'text.secondary', minWidth: 32, textAlign: 'center' }} title="Reset to 100%">{zoom}%</Typography>
                    <IconButton size="small" onClick={() => setZoom((z) => Math.min(150, z + 10))} title="Zoom in"><ZoomInIcon sx={{ fontSize: 15 }} /></IconButton>
                </Box>
            </Box>
            {showOriginal && <SourceStrip activeRef={activeRef} lines={lines} />}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: pdfBody ? 0 : 2, bgcolor: '#f3f4f3' }}>
                <Box sx={pdfBody ? { width: '100%', height: '100%' } : { transform: zoom === 100 ? 'none' : `scale(${zoom / 100})`, transformOrigin: 'top center', width: '100%', height: 'auto', minWidth: '100%' }}>
                    {showOriginal ? originalBody ?? <OriginalNotice>Preview not supported for this format</OriginalNotice> : <ExtractedViewer raw={raw} lines={lines} activeRef={activeRef} onPickLine={onPickLine} filename={filename} />}
                </Box>
            </Box>
        </Box>
    );
}
